import React from "react";
import AppHeading from "../layout/parts/app-heading";
import AppBody from "../layout/parts/app-body";
import Card from "../components/card";
import ReactDatePicker from "react-datepicker";
import Select from "../common/select";
import axios, { errorHandler } from "../requests/axios.js";
import dateToYmd from "../functions/date-to-ymd.js";
import hasPermission, { PERMISSIONS } from "../functions/has-permission";
import { useAuth } from "../providers/auth-provider.jsx";
import { toast } from "react-toastify";
import { subDays, isValid, min, max } from "date-fns";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import SlowMotionVideoRoundedIcon from "@mui/icons-material/SlowMotionVideoRounded";

const GROUP_BY_OPTIONS = [
  { label: "By Title", value: "title" },
  { label: "By Provider", value: "provider" },
];

const AVAILABLE_REPORTS = [
  {
    key: "avod",
    title: "AVOD Report",
    description:
      "Minutes watched, impressions and requests for your AVOD content, by title or by provider.",
    icon: <SlowMotionVideoRoundedIcon />,
    permission: PERMISSIONS.AVOD,
  },
];

export default function ReportsPage() {
  const NO_REPORTS_TEXT = `There are no reports available for your account yet, if you feel like this is a mistake please reach out to us.`;
  const { user } = useAuth();
  const [selectedReport, setSelectedReport] = React.useState(null);
  const [dateRange, setDateRange] = React.useState({
    start: subDays(new Date(), 30),
    end: new Date(),
  });
  const [groupBy, setGroupBy] = React.useState(GROUP_BY_OPTIONS[0]);
  const [downloading, setDownloading] = React.useState(false);

  const reports = AVAILABLE_REPORTS.filter((report) =>
    hasPermission(user, report.permission)
  );

  function onDateChange(date, field) {
    if (!(date instanceof Date) || !isValid(date)) {
      return;
    }

    setDateRange((prev) => {
      const start = field === "start" ? date : prev.start;
      const end = field === "end" ? date : prev.end;

      return { start: min([start, end]), end: max([start, end]) };
    });
  }

  function downloadAvodReport() {
    const startDate = dateToYmd(dateRange.start);
    const endDate = dateToYmd(dateRange.end);

    setDownloading(true);
    axios
      .get("api/reports/avod", {
        params: {
          start_date: startDate,
          end_date: endDate,
          group_by: groupBy.value,
        },
        responseType: "blob",
      })
      .then((response) => {
        const url = URL.createObjectURL(response.data);
        const link = document.createElement("a");
        link.href = url;
        link.download = `avod-report-by-${groupBy.value}_${startDate}_${endDate}.csv`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
      })
      .catch(
        errorHandler((e) => {
          console.error(e);
          toast.error("Error downloading AVOD report");
        })
      )
      .finally(() => setDownloading(false));
  }

  return (
    <React.Fragment>
      <AppHeading />

      <AppBody>
        <div className="reports-page">
          {reports.length === 0 ? (
            <div className="reports-page__empty">{NO_REPORTS_TEXT}</div>
          ) : selectedReport === null ? (
            <div className="reports-page__list">
              {reports.map((report) => (
                <button
                  key={report.key}
                  className="reports-page__report-card"
                  onClick={() => setSelectedReport(report)}
                >
                  {report.icon}
                  <span className="reports-page__report-title">
                    {report.title}
                  </span>
                  <span className="reports-page__report-description">
                    {report.description}
                  </span>
                </button>
              ))}
            </div>
          ) : (
            <div className="reports-page__detail">
              <div className="reports-page__detail-header">
                <button
                  className="btn btn--inverse-primary"
                  onClick={() => setSelectedReport(null)}
                >
                  <ArrowBackRoundedIcon />
                  All reports
                </button>
              </div>

              <Card fit>
                <div className="reports-page__report-form">
                  <h2>{selectedReport.title}</h2>
                  <p>{selectedReport.description}</p>

                  <div className="reports-page__filters">
                    <Select
                      name="group_by"
                      options={GROUP_BY_OPTIONS}
                      defaultValue={GROUP_BY_OPTIONS[0]}
                      onChange={(option) => setGroupBy(option)}
                      classNamePrefix="react-select"
                    />
                    <CalendarMonthRoundedIcon />
                    <ReactDatePicker
                      selected={dateRange.start}
                      onChange={(date) => onDateChange(date, "start")}
                      dateFormat="yyyy-MM-dd"
                    />
                    <ArrowForwardRoundedIcon />
                    <ReactDatePicker
                      selected={dateRange.end}
                      onChange={(date) => onDateChange(date, "end")}
                      dateFormat="yyyy-MM-dd"
                    />
                  </div>

                  <div className="reports-page__actions">
                    <button
                      className="btn btn--primary"
                      onClick={downloadAvodReport}
                      disabled={downloading}
                    >
                      <DownloadRoundedIcon />
                      {downloading ? "Preparing..." : "Download CSV"}
                    </button>
                  </div>
                </div>
              </Card>
            </div>
          )}
        </div>
      </AppBody>
    </React.Fragment>
  );
}
